'use client';

/**
 * Layout Offset Context & Hook
 *
 * Provides the current header/tabs offset to components that need to position
 * themselves relative to the viewport (e.g., SplitPanel with fixed positioning).
 *
 * Usage:
 * 1. Wrap your layout content with LayoutOffsetProvider
 * 2. Use useLayoutOffset() in components that need the offset value
 *
 * @example
 * ```tsx
 * // In Layout.tsx
 * <LayoutOffsetProvider offset={headerHeight + tabsHeight + breadcrumbsHeight}>
 *   {children}
 * </LayoutOffsetProvider>
 *
 * // In SplitPanel (automatically uses context)
 * const { offset } = useLayoutOffset();
 * // Uses offset for fixed positioning
 * ```
 */

import type React from 'react';
import { createContext, type ReactNode, useContext, useMemo } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface LayoutOffsetContextValue {
  /**
   * Total offset from viewport top to the content area (in pixels).
   * Includes header, tabs, and breadcrumbs area.
   */
  offset: number;

  /**
   * Whether the offset is provided by a parent LayoutOffsetProvider.
   * If false, the component should fall back to its own headerOffset prop.
   */
  hasProvider: boolean;
}

export interface LayoutOffsetProviderProps {
  /**
   * Total offset from viewport top to the content area (in pixels).
   * Calculate this based on your layout structure:
   * - Header/AppBar height (typically 64px)
   * - Tabs height when visible (typically 48px)
   * - Breadcrumbs area (typically 40px)
   */
  offset: number;

  children: ReactNode;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Default layout measurements (in pixels).
 * These match the standard MUI component heights.
 */
export const LAYOUT_HEIGHTS = {
  /** MUI Toolbar with page header (title + description) */
  TOOLBAR_WITH_HEADER: 64,
  /** AppNavBar toolbar height (matches HEADER_HEIGHT) */
  TOOLBAR_MINIMAL: 44,
  /** PageTabBar height */
  TAB_BAR: 48,
  /** Breadcrumbs area with padding */
  BREADCRUMBS: 40,
} as const;

// ============================================================================
// Context
// ============================================================================

const defaultContextValue: LayoutOffsetContextValue = {
  offset: 0,
  hasProvider: false,
};

export const LayoutOffsetContext = createContext<LayoutOffsetContextValue>(defaultContextValue);

// ============================================================================
// Provider
// ============================================================================

/**
 * Provides layout offset to descendant components.
 *
 * Place this in your Layout component, wrapping the content area.
 * Calculate the offset based on your header/tabs/breadcrumbs heights.
 */
export function LayoutOffsetProvider({ offset, children }: LayoutOffsetProviderProps): React.JSX.Element {
  const value = useMemo<LayoutOffsetContextValue>(
    () => ({
      offset,
      hasProvider: true,
    }),
    [offset],
  );

  return <LayoutOffsetContext.Provider value={value}>{children}</LayoutOffsetContext.Provider>;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Get the current layout offset from context.
 *
 * Returns { offset, hasProvider } where:
 * - offset: The total offset in pixels from viewport top to content area
 * - hasProvider: Whether a LayoutOffsetProvider exists in the tree
 *
 * Components should check hasProvider and fall back to their own
 * headerOffset prop if no provider is present.
 */
export function useLayoutOffset(): LayoutOffsetContextValue {
  return useContext(LayoutOffsetContext);
}

/**
 * Get the layout offset value, with an optional fallback.
 *
 * @param fallback - Value to use if no LayoutOffsetProvider is present
 * @returns The offset in pixels
 */
export function useLayoutOffsetValue(fallback = 0): number {
  const { offset, hasProvider } = useLayoutOffset();
  return hasProvider ? offset : fallback;
}
